import {
  FlexBox,
  Icon,
  Skeleton,
  Table,
  Text,
  Tooltip,
} from '@dixa/lib-shared-habla';
import { typography } from '@dixa/lib-shared-tokens';
import classNames from 'classnames';
import { DashboardFavorite, QueueMetrics } from 'lib-dashboard-types';
import { memo, useMemo } from 'react';

import { dashboardApi } from 'apis/dashboard';
import { AppLink } from 'components/appLink';
import { useServerStorage } from 'utils/useServerStorage';
import { getUserDisplayName, useCurrentUser } from 'utils/user';

import { DashboardCard } from '../../../../components/card';
import { QueueDashboardMetrics } from '../../../queue/components/queues/components/metrics/queueDashboardMetrics';
import {
  QueueDashboardQueueContext,
  QueueDashboardQueueContextValue,
} from '../../../queue/components/queues/components/queue/queueDashboardQueue.context';
import { useAggregateQueueMetrics } from '../../utils';

import { FavoriteDashboardFavoriteActions } from './components/actions/favoriteDashboardFavoriteActions';
import styles from './favoriteDashboardFavorite.module.scss';

type FavoriteDashboardFavoriteProps = {
  /** The favorite to display. */
  favorite: DashboardFavorite;
  /**
   * Pre-fetched queue metrics. When provided, the component skips its own fetch
   * and uses these directly.
   */
  queueMetrics?: readonly QueueMetrics[];
  /**
   * Whether the metrics are still loading (used when queueMetrics is provided
   * externally).
   */
  isSkeleton?: boolean;
  /**
   * Whether to visually highlight the card with a purple accent. Defaults to
   * true (aggregate summary style).
   */
  highlighted?: boolean;
};

export const FavoriteDashboardFavorite = memo(
  function FavoriteDashboardFavorite({
    favorite,
    ...props
  }: FavoriteDashboardFavoriteProps): JSX.Element {
    const { user } = useCurrentUser();
    const storage = useServerStorage('user');
    const { layout = 'default' } = storage.data.dashboard ?? {};

    const queueIds = useMemo(
      () => favorite.params.queue_ids ?? [],
      [favorite.params.queue_ids],
    );

    // Self-fetch metrics when not provided externally.
    const { queueMetrics, isUninitialized, isFetching } =
      dashboardApi.useReadQueueDashboardMetricsQuery(
        { queueIds },
        {
          skip: !queueIds.length || !!props.queueMetrics,
          selectFromResult: ({ data, ...rest }) => ({
            ...rest,
            queueMetrics: data?.data.queue_metrics ?? [],
          }),
        },
      );

    const { aggregateMetrics } = useAggregateQueueMetrics({
      queueMetrics: props.queueMetrics ?? queueMetrics,
      queueIds,
    });

    const aggregateQueueMetrics: QueueMetrics | undefined = useMemo(
      () =>
        aggregateMetrics
          ? {
              id: 'aggregate',
              metrics: aggregateMetrics,
              sla_policies: [],
            }
          : undefined,
      [aggregateMetrics],
    );

    const isSkeleton = props.isSkeleton ?? (isUninitialized || isFetching);

    const titleSlot = useMemo(
      (): JSX.Element => (
        <FlexBox alignItems="center" gap="200">
          <FlexBox
            justifyContent="center"
            alignItems="center"
            width="32px"
            height="32px"
          >
            {isSkeleton ? (
              <Skeleton height="18px" width="18px" />
            ) : (
              <Tooltip
                content="These metrics represent aggregate data from the queues in this favorite list."
                side="right"
                wrap="div"
                wrapProps={{ style: { display: 'flex' } }}
              >
                <Icon name="world" color={typography.color.secondary} />
              </Tooltip>
            )}
          </FlexBox>

          {isSkeleton ? (
            <Skeleton height="20px" width="120px" />
          ) : (
            <div>
              <FlexBox alignItems="center" gap="200">
                <AppLink
                  path="/dashboard/queues"
                  params={{
                    search: {
                      type: 'favourite',
                      id: favorite.id,
                    },
                  }}
                >
                  <Text.Heading variant="heading-3">
                    {favorite.name}
                  </Text.Heading>
                </AppLink>

                {favorite.shared && (
                  <Tooltip
                    content={
                      favorite.user.id === user.id
                        ? 'Shared by you'
                        : `Shared with you by ${getUserDisplayName(favorite.user)}`
                    }
                  >
                    <Icon name="users" color="tertiary" />
                  </Tooltip>
                )}
              </FlexBox>

              {layout === 'default' && favorite.description && (
                <Text.Paragraph variant="4" color="secondary">
                  {favorite.description}
                </Text.Paragraph>
              )}
            </div>
          )}
        </FlexBox>
      ),
      [favorite, isSkeleton, layout, user.id],
    );

    const highlighted = props.highlighted ?? true;

    const contextValue: QueueDashboardQueueContextValue = useMemo(
      () => ({
        index: 0,
        allQueueIds: queueIds,
        isSkeleton,
        layout,
        highlighted,
      }),
      [highlighted, isSkeleton, layout, queueIds],
    );

    const renderCompactLayout = (): JSX.Element => (
      <Table.Tr
        className={classNames({
          [styles.tr]: !isSkeleton && highlighted,
        })}
      >
        <Table.Td>{titleSlot}</Table.Td>

        <QueueDashboardMetrics queueMetrics={aggregateQueueMetrics} />

        <Table.Td>
          <FavoriteDashboardFavoriteActions
            favorite={favorite}
            isSkeleton={isSkeleton}
          />
        </Table.Td>
      </Table.Tr>
    );

    const renderDefaultLayout = (): JSX.Element => (
      <DashboardCard
        titleSlot={titleSlot}
        actionsSlot={
          <FavoriteDashboardFavoriteActions
            favorite={favorite}
            isSkeleton={isSkeleton}
          />
        }
        className={classNames({
          [styles.card]: !isSkeleton && highlighted,
        })}
      >
        <QueueDashboardMetrics queueMetrics={aggregateQueueMetrics} />
      </DashboardCard>
    );

    return (
      <QueueDashboardQueueContext.Provider value={contextValue}>
        {layout === 'compact' ? renderCompactLayout() : renderDefaultLayout()}
      </QueueDashboardQueueContext.Provider>
    );
  },
);
